import React, { useEffect, useState } from 'react';
import styles from './WordsSection.module.css';
import { LoaderIcon, BookIcon, HeartIcon, SearchIcon } from '../../Icons';

const WordsSection = ({
  wordsLoading,
  chapterWords,
  showAllWordDefinitions,
  onToggleAllWordDefinitions,
  showAllWordSentences,
  onToggleAllWordSentences,
  expandedSentences,
  onGoToChapter,
  favoriteLoadingMap,
  onFavoriteToggle,
  onShowWordDetail,
}) => {
  const [activeDictMap, setActiveDictMap] = useState({});

  const formatDefinitionValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      const parts = value
        .map((item) => formatDefinitionValue(item))
        .filter((item) => item && typeof item === 'string' && item.trim());
      return parts.join(' / ');
    }

    if (typeof value === 'object') {
      const enText = value.en || value.english || value.text_en || '';
      const zhText = value.zh || value.cn || value.chinese || value.text_zh || '';

      const extraParts = Object.entries(value)
        .filter(([key]) => !['en', 'english', 'text_en', 'zh', 'cn', 'chinese', 'text_zh'].includes(key))
        .map(([key, val]) => {
          if (typeof val === 'string') {
            return val;
          }
          if (Array.isArray(val)) {
            return val
              .map((item) => formatDefinitionValue(item))
              .filter((item) => item && typeof item === 'string' && item.trim())
              .join(' / ');
          }
          if (val && typeof val === 'object') {
            return formatDefinitionValue(val);
          }
          return '';
        })
        .filter((item) => item && item.trim());

      const parts = [];
      if (enText) parts.push(enText);
      if (zhText) parts.push(zhText);
      parts.push(...extraParts);

      if (parts.length > 0) {
        return parts.join(' / ');
      }

      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }

    return String(value);
  };

  const getYoudaoPayload = (data) => {
    if (!data || typeof data !== 'object') {
      return null;
    }
    if (data.youdao_definition && typeof data.youdao_definition === 'object') {
      return data.youdao_definition;
    }
    if (data.definitions) {
      return data;
    }
    return null;
  };

  const renderYoudaoDefinitions = (wrapper) => {
    const data = getYoudaoPayload(wrapper);
    if (!data || typeof data !== 'object') {
      const legacyHtml = wrapper?.edict_definition || wrapper?.edictDefinition;
      if (typeof legacyHtml === 'string' && legacyHtml.trim()) {
        return (
          <div
            className={styles.definitionContent}
            dangerouslySetInnerHTML={{ __html: legacyHtml }}
          />
        );
      }
      return <div className={styles.noDefinition}>暂无释义</div>;
    }

    const {
      exam_types: examTypes,
      variants,
      definitions,
      phrases,
      examples,
      synonyms,
      related_words: relatedWords,
      etymology,
      baike,
    } = data;

    const sections = [];

    const pushSection = (key, content) => {
      sections.push(
        <div key={key} className={styles.definitionGroupFull}>
          {content}
        </div>
      );
    };

    const simpleDefs = Array.isArray(definitions?.simple) ? definitions.simple : [];
    if (simpleDefs.length > 0) {
      pushSection('simple', (
        <>
          <h4 className={styles.sectionTitle}>基础释义</h4>
          <ul className={styles.definitionList}>
            {simpleDefs.map((item, index) => {
              const content = formatDefinitionValue(item);
              return (
                <li key={`simple-${index}`}>
                  {content || '—'}
                </li>
              );
            })}
          </ul>
          {Array.isArray(examTypes) && examTypes.length > 0 && (
            <div className={styles.tagList}>
              {examTypes.map((exam, idx) => (
                <span key={`exam-${idx}`} className={styles.tagChip}>{exam}</span>
              ))}
            </div>
          )}
        </>
      ));
    }

    if (Array.isArray(variants) && variants.length > 0) {
      pushSection('variants', (
        <>
          <h4 className={styles.sectionTitle}>词形变化</h4>
          <ul className={styles.bulletList}>
            {variants.map((variant, index) => (
              <li key={`variant-${index}`}>
                {variant.label && <strong>{variant.label}: </strong>}
                {variant.value}
              </li>
            ))}
          </ul>
        </>
      ));
    }

    if (Array.isArray(phrases) && phrases.length > 0) {
      pushSection('phrases', (
        <>
          <h4 className={styles.sectionTitle}>短语搭配</h4>
          {phrases.map((phrase, index) => (
            <div key={`phrase-${index}`} className={styles.definitionItem}>
              {phrase.term && <p className={styles.definitionTerm}>{phrase.term}</p>}
              {Array.isArray(phrase.translations) && phrase.translations.length > 0 && (
                <div className={styles.tagList}>
                  {phrase.translations.map((trans, idx) => (
                    <span key={`phrase-trans-${idx}`} className={styles.tagChip}>{trans}</span>
                  ))}
                </div>
              )}
              {Array.isArray(phrase.examples) && phrase.examples.length > 0 && (
                <ul className={styles.bulletList}>
                  {phrase.examples.map((example, exIdx) => (
                    <li key={`phrase-ex-${exIdx}`}>{example}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </>
      ));
    }

    const hasBilingualExamples = Array.isArray(examples?.bilingual) && examples.bilingual.length > 0;
    const hasAudioExamples = Array.isArray(examples?.audio) && examples.audio.length > 0;
    const hasAuthoritativeExamples = Array.isArray(examples?.authoritative) && examples.authoritative.length > 0;

    if (hasBilingualExamples || hasAudioExamples || hasAuthoritativeExamples) {
      pushSection('examples', (
        <>
          <h4 className={styles.sectionTitle}>例句</h4>
          {hasBilingualExamples && (
            <div className={styles.exampleGroup}>
              <h5>双语例句</h5>
              <ul className={styles.exampleList}>
                {examples.bilingual.map((example, idx) => (
                  <li key={`bilingual-${idx}`}>
                    {example.en && <p className={styles.exampleText}>{example.en}</p>}
                    {example.zh && <p className={styles.exampleTranslation}>{example.zh}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hasAudioExamples && (
            <div className={styles.exampleGroup}>
              <h5>音频例句</h5>
              <ul className={styles.exampleList}>
                {examples.audio.map((example, idx) => (
                  <li key={`audio-${idx}`}>
                    {example.sentence && <p className={styles.exampleText}>{example.sentence}</p>}
                    <div className={styles.audioMeta}> 
                      {example.source && <span>来源：{example.source}</span>}
                      {example.duration && <span>时长：{example.duration}</span>}
                    </div>
                    {example.audio && (
                      <audio className={styles.audioPlayer} controls src={example.audio} preload="none">
                        您的浏览器不支持音频播放。
                      </audio>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hasAuthoritativeExamples && (
            <div className={styles.exampleGroup}>
              <h5>权威例句</h5>
              <ul className={styles.exampleList}>
                {examples.authoritative.map((example, idx) => (
                  <li key={`authoritative-${idx}`}>
                    {example.sentence && <p className={styles.exampleText}>{example.sentence}</p>}
                    {example.source && <p className={styles.metaItem}>来源：{example.source}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ));
    }

    const hasSynonyms = Array.isArray(synonyms) && synonyms.length > 0;
    if (hasSynonyms) {
      pushSection('synonyms', (
        <>
          <h4 className={styles.sectionTitle}>近义词</h4>
          {synonyms.map((entry, idx) => (
            <div key={`syn-${idx}`} className={styles.definitionItem}>
              <div className={styles.entryHeader}>
                {entry.pos && <span className={styles.partOfSpeech}>{entry.pos}</span>}
                {entry.meaning && <span className={styles.metaItem}>{entry.meaning}</span>}
              </div>
              {Array.isArray(entry.words) && entry.words.length > 0 && (
                <div className={styles.tagList}>
                  {entry.words.map((word, wordIdx) => (
                    <span key={`syn-word-${wordIdx}`} className={styles.tagChip}>{word}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      ));
    }

    const hasRelatedWords = Array.isArray(relatedWords) && relatedWords.length > 0;
    if (hasRelatedWords) {
      pushSection('related', (
        <>
          <h4 className={styles.sectionTitle}>相关词</h4>
          {relatedWords.map((entry, idx) => (
            <div key={`rel-${idx}`} className={styles.definitionItem}>
              {entry.pos && <span className={styles.partOfSpeech}>{entry.pos}</span>}
              {Array.isArray(entry.words) && entry.words.length > 0 && (
                <ul className={styles.bulletList}>
                  {entry.words.map((wordItem, wordIdx) => (
                    <li key={`rel-word-${wordIdx}`}>
                      <span className={styles.relatedWord}>{wordItem.word}</span>
                      {wordItem.definition && <span className={styles.relatedDefinition}> - {wordItem.definition}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </>
      ));
    }

    const hasEtymology = Array.isArray(etymology) && etymology.length > 0;
    if (hasEtymology) {
      pushSection('etymology', (
        <>
          <h4 className={styles.sectionTitle}>词源</h4>
          <div className={styles.etymologyList}>
            {etymology.map((item, idx) => (
              <div key={`etym-${idx}`} className={styles.etymologyItem}>
                {item.word && <p className={styles.definitionTerm}>{item.word}</p>}
                {item.description && <p className={styles.definitionText}>{item.description}</p>}
                {(item.source || item.url) && (
                  <p className={styles.metaItem}>
                    来源：{item.source}
                    {item.url && (
                      <>
                        {' '}
                        <a href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
                      </>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      ));
    }

    const hasBaike = baike && (
      (Array.isArray(baike.summaries) && baike.summaries.length > 0) ||
      baike.source
    );
    if (hasBaike) {
      pushSection('baike', (
        <>
          <h4 className={styles.sectionTitle}>百科</h4>
          {Array.isArray(baike.summaries) && baike.summaries.length > 0 && (
            <ul className={styles.bulletList}>
              {baike.summaries.map((summary, idx) => (
                <li key={`summary-${idx}`}>{summary}</li>
              ))}
            </ul>
          )}
          {baike.source && (
            <p className={styles.metaItem}>
              来源：
              {baike.source.url ? (
                <a href={baike.source.url} target="_blank" rel="noreferrer">{baike.source.name || baike.source.url}</a>
              ) : (
                baike.source.name
              )}
            </p>
          )}
        </>
      ));
    }

    if (sections.length === 0) {
      return <div className={styles.noDefinition}>暂无释义</div>;
    }

    return <div className={styles.definitionContentFull}>{sections}</div>;
  };

  const renderCollinsDefinition = (wrapper) => {
    const collinsHtml = typeof wrapper?.collins_definition === 'string'
      ? wrapper.collins_definition.trim()
      : '';
    if (!collinsHtml) {
      return <div className={styles.noDefinition}>暂无释义</div>;
    }
    return (
      <div
        className={styles.definitionContent}
        dangerouslySetInnerHTML={{ __html: collinsHtml }}
      />
    );
  };

  useEffect(() => {
    const validIds = new Set((chapterWords || []).map((item, idx) => String(item.id ?? idx)));
    setActiveDictMap(prev => {
      let changed = false;
      const next = {};
      Object.keys(prev).forEach(key => {
        if (validIds.has(key)) {
          next[key] = prev[key];
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [chapterWords]);

  const getActiveDictForWord = (wordId, hasYoudao, hasCollins) => {
    const stored = activeDictMap[wordId];
    if (stored === 'collins' && hasCollins) return 'collins';
    if (stored === 'youdao' && hasYoudao) return 'youdao';
    if (hasYoudao) return 'youdao';
    if (hasCollins) return 'collins';
    return 'youdao';
  };

  const renderWordCard = (wordItem, index) => {
    const wordData = wordItem.word_jsondata || {};
    const wordId = wordItem.id || index;
    const wordKey = String(wordId);
    const sentenceDetail = expandedSentences[wordId] || {};
    const isLoading = !!favoriteLoadingMap?.[wordId];
    const wordText = wordItem.word;
    const youdaoData = getYoudaoPayload(wordData);
    const collinsHtml = typeof wordData.collins_definition === 'string'
      ? wordData.collins_definition.trim()
      : '';
    const hasYoudao = Boolean(youdaoData);
    const hasCollins = Boolean(collinsHtml);
    const activeDict = getActiveDictForWord(wordKey, hasYoudao, hasCollins);

    return (
      <div key={wordId} className={styles.wordCard}>
        <div className={styles.wordHeader}>
          <div className={styles.wordInfo}>
            <div className={styles.wordTitleRow}>
              <h3 className={styles.wordTitle}>{wordText}</h3>
              <button
                type="button"
                className={styles.wordDetailButton}
                onClick={() => onShowWordDetail?.(wordItem)}
                title="查看详细释义"
              >
                <SearchIcon size={20} />
              </button>
              <div className={styles.wordActions}>
                <button
                  type="button"
                  className={`${styles.favoriteButton} ${styles.favoriteButtonActive}`}
                  onClick={() => onFavoriteToggle?.(wordItem)}
                  disabled={isLoading}
                  title="取消收藏"
                  aria-label="取消收藏"
                >
                  {isLoading ? <LoaderIcon size={20} /> : <HeartIcon size={20} filled />}
                </button>
                {/* <button
                  className={styles.gotoChapterBtn}
                  onClick={() => onGoToChapter?.(wordItem.chapter_id, wordItem.sentence_id)}
                >
                  查看章节
                </button> */}
              </div>
            </div>
            {youdaoData?.definitions?.simple?.length > 0 && (
              <div className={styles.wordSimpleDefinition}>
                {youdaoData.definitions.simple.slice(0, 2).map((item, idx) => (
                  <p key={`simple-${wordId}-${idx}`} className={styles.simpleDefinitionLine}>
                    {item}
                  </p>
                ))}
              </div>
            )}
            {(!youdaoData || youdaoData?.definitions?.simple?.length === 0) && (
              <div className={styles.wordSimpleDefinition}>
                <p className={styles.simpleDefinitionLine}>暂无基础释义，点击放大镜查看详情</p>
              </div>
            )}
          </div>
        </div>

        {showAllWordSentences && (sentenceDetail.originalText || sentenceDetail.translatedText) && (
          <div className={styles.sentenceDetail}>
            <div className={styles.sentenceDetailContent}>
              {sentenceDetail.originalText && (
                <div className={styles.sentenceOriginal}>
                  <p className={styles.contentText}>{sentenceDetail.originalText}</p>
                </div>
              )}
              {sentenceDetail.translatedText && (
                <div className={styles.sentenceTranslation}>
                  <p className={styles.contentText}>{sentenceDetail.translatedText}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {showAllWordDefinitions && (
          <div className={styles.wordDefinitions}>
            {(hasYoudao || hasCollins) && (
              <div className={styles.wordDictTabs}>
                {hasYoudao && (
                  <button
                    type="button"
                    className={`${styles.wordDictTab} ${activeDict === 'youdao' ? styles.wordDictTabActive : ''}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveDictMap(prev => ({
                        ...prev,
                        [wordKey]: 'youdao',
                      }));
                    }}
                  >
                    Youdao
                  </button>
                )}
                {hasCollins && (
                  <button
                    type="button"
                    className={`${styles.wordDictTab} ${activeDict === 'collins' ? styles.wordDictTabActive : ''}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveDictMap(prev => ({
                        ...prev,
                        [wordKey]: 'collins',
                      }));
                    }}
                  >
                    Collins
                  </button>
                )}
              </div>
            )}
            <div className={styles.wordDictContent}>
              {activeDict === 'youdao' && hasYoudao && renderYoudaoDefinitions(wordData)}
              {activeDict === 'collins' && hasCollins && renderCollinsDefinition(wordData)}
              {!hasYoudao && !hasCollins && (
                <div className={styles.noDefinition}>暂无释义</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (wordsLoading) {
      return (
        <div className={styles.loading}>
          <LoaderIcon size={20} />
          <p>加载单词列表中...</p>
        </div>
      );
    }

    if (!chapterWords || chapterWords.length === 0) {
      return (
        <div className={styles.emptyState}>
          <BookIcon size={48} />
          <h3>暂无收藏单词</h3>
          <p>点击文章中的单词进行收藏，收藏的单词会显示在这里</p>
        </div>
      );
    }

    return <div className={styles.wordsList}>{chapterWords.map(renderWordCard)}</div>;
  };

  return (
    <div className={styles.container}>
      {renderContent()}
    </div>
  );
};

export default WordsSection;
